import type { ReactElement } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge, Stack, Surface, Text, ValueUnavailable } from "../ui-kit";
import type { ChannelMetric, ChannelMetricMap, MetricChannel, MetricEnvelope, MetricGranularity } from "./fixtures";
import styles from "./GrowthChart.module.css";
import { useChartTheme } from "./useChartTheme";

const CHANNEL_LABEL: Readonly<Record<MetricChannel, string>> = { mobile: "Mobile", telegram: "Telegram" };

type GrowthChartRow = { period: string } & Partial<Record<MetricChannel, number>>;

function envelopeOf(metric: ChannelMetric): MetricEnvelope {
  if (!metric.available) {
    throw new Error("GrowthChart: channel has no envelope to plot");
  }
  return metric.envelope;
}

function availableChannels(envelope: ChannelMetricMap): readonly MetricChannel[] {
  return (Object.keys(envelope) as MetricChannel[]).filter((channel) => envelope[channel].available);
}

interface UnavailableChannel {
  readonly channel: MetricChannel;
  readonly reason: string;
}

function unavailableChannels(envelope: ChannelMetricMap): readonly UnavailableChannel[] {
  const result: UnavailableChannel[] = [];
  for (const channel of Object.keys(envelope) as MetricChannel[]) {
    const metric = envelope[channel];
    if (!metric.available) {
      result.push({ channel, reason: metric.reason });
    }
  }
  return result;
}

/** Periods are the same list across every available channel (`fixtures.ts`
 * builds each envelope from one shared `DAY_PERIODS`/`MONTH_PERIODS`
 * array), so a row at a given index is the same period for every channel
 * plotted on it. */
function toRows(envelope: ChannelMetricMap, plotted: readonly MetricChannel[]): readonly GrowthChartRow[] {
  const first = plotted[0];
  if (first === undefined) {
    return [];
  }
  const firstSeries = envelopeOf(envelope[first]).series;
  return firstSeries.map((point, index) => {
    const row: GrowthChartRow = { period: point.period };
    for (const channel of plotted) {
      const series = envelopeOf(envelope[channel]).series;
      const channelPoint = series[index];
      if (channelPoint === undefined || channelPoint.period !== point.period) {
        throw new Error(`GrowthChart: ${channel} period mismatch at index ${index}`);
      }
      row[channel] = channelPoint.value;
    }
    return row;
  });
}

function channelSummary(channel: MetricChannel, metric: ChannelMetric): string {
  const label = CHANNEL_LABEL[channel];
  return metric.available ? `${metric.envelope.total} ${label}` : `${label} not available — ${metric.reason}`;
}

export interface GrowthChartProps {
  readonly title: string;
  readonly granularity: MetricGranularity;
  readonly envelope: ChannelMetricMap;
  readonly isFixture?: boolean;
}

/**
 * Shared renderer behind the two T3 growth charts (ISSUE-352 rescoping) — a
 * time series line chart over a fixture envelope, plotting only the
 * channels that are `available` (Architect decision 2026-09-03: a channel
 * with no observable event, e.g. Telegram registration, is never plotted as
 * a line, never counted into a total, and never silently dropped — it gets
 * an explicit {@link ValueUnavailable} note instead). `role="img"` + a
 * summary `aria-label` on the plot region is the chart's WCAG 1.1.1 text
 * alternative and includes each unavailable channel's reason, not just its
 * plotted totals; the visible `Text` heading gives sighted users the same
 * title, and the unavailable note below the chart repeats the reason for
 * both sighted and screen-reader users.
 */
export function GrowthChart({ title, granularity, envelope, isFixture = false }: GrowthChartProps): ReactElement {
  const theme = useChartTheme();
  const channels = Object.keys(envelope) as MetricChannel[];
  const plotted = availableChannels(envelope);
  const missing = unavailableChannels(envelope);
  const rows = toRows(envelope, plotted);
  const periodLabel = granularity === "day" ? "day" : "month";
  const channelSummaries = channels.map((channel) => channelSummary(channel, envelope[channel])).join(", ");
  const summary = `${title}: ${channelSummaries}, over ${rows.length} ${periodLabel} periods`;

  return (
    <Surface as="section">
      <Stack gap={4}>
        <Stack direction="row" gap={2} align="center" wrap>
          <Text variant="heading" as="h3">
            {title}
          </Text>
          {isFixture ? <Badge tone="info">Sample data</Badge> : null}
        </Stack>
        <div className={styles.chartContainer} role="img" aria-label={summary}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" />
              <XAxis
                dataKey="period"
                stroke={theme.axis}
                tick={{ fill: theme.axis, fontSize: 12 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis stroke={theme.axis} tick={{ fill: theme.axis, fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: theme.tooltipBg,
                  border: `1px solid ${theme.tooltipBorder}`,
                  color: theme.tooltipFg,
                }}
              />
              <Legend />
              {plotted.map((channel) => (
                <Line
                  key={channel}
                  type="monotone"
                  dataKey={channel}
                  name={CHANNEL_LABEL[channel]}
                  stroke={theme[channel]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {missing.map(({ channel, reason }) => (
          <Text key={channel} as="p" variant="caption">
            <ValueUnavailable label={`${CHANNEL_LABEL[channel]} not available`} reason={reason} />
          </Text>
        ))}
      </Stack>
    </Surface>
  );
}
